import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { edgeOnboardApi } from '../../api/services';
import type { EdgeOnboardSlackMention } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import { EoIcon, formatEoDateTime } from './eoShared';

// Mirrors EdgeOnboard::Setting (webhook-server) — the server is authoritative and
// 422s if these are bypassed; the client copy is so Save can be blocked before a
// round trip and each row can show its own error.
export const SLACK_MEMBER_ID_FORMAT = /^[UW][A-Z0-9]{8,12}$/;
export const SLACK_LABEL_MAX_LENGTH = 60;
export const SLACK_MENTIONS_MAX = 20;

export const SETTINGS_QUERY_KEY = ['edge-onboard', 'settings'] as const;

interface Row {
  key: number;
  label: string;
  member_id: string;
}

interface RowErrors {
  label?: string;
  member_id?: string;
}

/** Trim + upcase the ID the way the server will, so what you see is what gets stored. */
export const normalizeMention = (row: { label: string; member_id: string }): EdgeOnboardSlackMention => ({
  label: row.label.trim(),
  member_id: row.member_id.trim().toUpperCase(),
});

const isBlankRow = (row: { label: string; member_id: string }) =>
  row.label.trim() === '' && row.member_id.trim() === '';

export function validateMentions(rows: Row[]): { errors: Map<number, RowErrors>; listError: string | null } {
  const errors = new Map<number, RowErrors>();
  const kept = rows.filter(r => !isBlankRow(r));
  const seen = new Map<string, number>();

  kept.forEach(row => {
    const { label, member_id } = normalizeMention(row);
    const rowErrors: RowErrors = {};
    if (label === '') rowErrors.label = 'Label is required';
    else if (label.length > SLACK_LABEL_MAX_LENGTH) rowErrors.label = `${SLACK_LABEL_MAX_LENGTH} characters max`;
    if (!SLACK_MEMBER_ID_FORMAT.test(member_id)) {
      rowErrors.member_id = 'Must be a Slack member ID like U0123ABCDE';
    } else if (seen.has(member_id)) {
      rowErrors.member_id = 'Already in the list';
    } else {
      seen.set(member_id, row.key);
    }
    if (rowErrors.label || rowErrors.member_id) errors.set(row.key, rowErrors);
  });

  const listError =
    kept.length > SLACK_MENTIONS_MAX ? `At most ${SLACK_MENTIONS_MAX} people can be mentioned.` : null;
  return { errors, listError };
}

interface SlackMentionsDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Who gets @-mentioned on the "Onboarding started" / "ready for review" Slack
// posts (sc-6383). One global list, applied to both; the notifier reads it at
// send time so a save here is live on the next notification.
export const SlackMentionsDialog: React.FC<SlackMentionsDialogProps> = ({ open, onClose, onSaved }) => {
  const queryClient = useQueryClient();
  const [rows, setRows] = React.useState<Row[]>([]);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const nextKey = React.useRef(1);

  const settingsQuery = useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: () => edgeOnboardApi.settings.get(),
    enabled: open,
  });

  // Seed the editable rows from the saved list each time the dialog opens (or
  // the query lands while it is open); a cancelled edit never leaks forward.
  React.useEffect(() => {
    if (!open) return;
    setServerError(null);
    const saved = settingsQuery.data?.slack_mentions;
    if (saved) {
      setRows(saved.map(m => ({ key: nextKey.current++, label: m.label, member_id: m.member_id })));
    }
  }, [open, settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (slack_mentions: EdgeOnboardSlackMention[]) => edgeOnboardApi.settings.update({ slack_mentions }),
    onSuccess: saved => {
      queryClient.setQueryData(SETTINGS_QUERY_KEY, saved);
      void queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      onSaved();
    },
    onError: err => setServerError(extractApiError(err, 'Failed to save Slack notification settings')),
  });

  const { errors, listError } = React.useMemo(() => validateMentions(rows), [rows]);
  const isValid = errors.size === 0 && listError === null;

  const updateRow = (key: number, patch: Partial<Pick<Row, 'label' | 'member_id'>>) =>
    setRows(current => current.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: number) => setRows(current => current.filter(r => r.key !== key));
  const addRow = () => {
    // Mint the key OUTSIDE the updater: React may re-run updaters, and an impure
    // one would hand the row a fresh key on every re-run, orphaning edits.
    const key = nextKey.current++;
    setRows(current => [...current, { key, label: '', member_id: '' }]);
  };

  const submit = () => {
    if (!isValid) return;
    setServerError(null);
    // Blank rows are dropped here, not flagged — an empty row is just "nobody".
    saveMutation.mutate(rows.filter(r => !isBlankRow(r)).map(normalizeMention));
  };

  const saved = settingsQuery.data;
  const busy = saveMutation.isPending;

  return (
    // Dialogs portal to <body>, outside the page's .eo-scope wrapper — the
    // paper re-applies the scope class so ea-* styles work inside.
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'eo-scope' }}
      aria-labelledby="slack-mentions-dialog-title"
    >
      <DialogTitle id="slack-mentions-dialog-title" sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Slack notifications
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
          These people are @-mentioned in Slack when any onboarding starts or completes. Leave the list
          empty to post without mentions.
        </p>

        {serverError && <Alert severity="error">{serverError}</Alert>}
        {settingsQuery.error && (
          <Alert severity="error">
            {extractApiError(settingsQuery.error, 'Failed to load Slack notification settings')}
          </Alert>
        )}

        {settingsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {rows.length === 0 && (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                Nobody is mentioned yet.
              </p>
            )}
            {rows.map((row, index) => {
              const rowErrors = errors.get(row.key);
              return (
                <Box key={row.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Label"
                    size="small"
                    value={row.label}
                    onChange={e => updateRow(row.key, { label: e.target.value })}
                    error={Boolean(rowErrors?.label)}
                    helperText={rowErrors?.label ?? ' '}
                    inputProps={{ 'aria-label': `Label ${index + 1}`, maxLength: SLACK_LABEL_MAX_LENGTH + 1 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Member ID"
                    size="small"
                    value={row.member_id}
                    onChange={e => updateRow(row.key, { member_id: e.target.value })}
                    error={Boolean(rowErrors?.member_id)}
                    helperText={rowErrors?.member_id ?? ' '}
                    inputProps={{
                      'aria-label': `Member ID ${index + 1}`,
                      spellCheck: false,
                      style: { fontFamily: 'var(--edge-font-mono)' },
                    }}
                    sx={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="ea-icon-btn"
                    aria-label={`Remove ${row.label.trim() || `person ${index + 1}`}`}
                    onClick={() => removeRow(row.key)}
                    disabled={busy}
                    style={{ marginTop: 4 }}
                  >
                    <EoIcon name="close" />
                  </button>
                </Box>
              );
            })}
            {listError && <Alert severity="warning">{listError}</Alert>}
            <div>
              <button type="button" className="ea-btn outlined small" onClick={addRow} disabled={busy}>
                <EoIcon name="person_add" />
                Add person
              </button>
            </div>
          </Box>
        )}

        <p className="ea-helper" style={{ margin: 0 }}>
          To find a member ID in Slack: open the person&apos;s profile, click the ⋯ menu, then
          &ldquo;Copy member ID&rdquo;. It looks like <span className="mono">U0123ABCDE</span>. Plain @names
          don&apos;t notify anyone.
        </p>

        {saved?.updated_by_email && (
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            Last updated by {saved.updated_by_email} on {formatEoDateTime(saved.updated_at)}
          </p>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!isValid || busy || settingsQuery.isLoading || Boolean(settingsQuery.error)}
          onClick={submit}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
